import { alpha, Box, Link, Typography } from '@mui/material';

import { BUTTON_RADIUS } from '~/theme';

type AppBarButtonProps = {
  active?: boolean;
  // Accessible name for the rendered control. The visible `label` is plain
  // text content, which screen readers and `getByLabelText` don't treat as a
  // reliable accessible name once the button carries an icon — pass this when
  // the control needs an explicit, queryable label.
  'aria-label'?: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  label: React.ReactNode;
  onClick?: React.MouseEventHandler;
  // Draws the pill as a bordered, tinted control rather than bare chrome, for the one
  // action in the bar that has to be found rather than looked for (Ask Lift AI).
  outlined?: boolean;
  // `vertical` (default): the app-bar tile, laid out as a pill (see below).
  // `horizontal`: icon beside a left-aligned label, full width — for menu rows
  // (e.g. the mobile overflow).
  orientation?: 'vertical' | 'horizontal';
  pathname?: string;
};

export default function AppBarButton({
  icon,
  label,
  disabled = false,
  onClick,
  orientation = 'vertical',
  outlined = false,
  pathname,
  active,
  'aria-label': ariaLabel,
}: AppBarButtonProps) {
  const HOVER_COLOR = 'primary.light';
  const ACTIVE_COLOR = 'primary.main';
  const isHorizontal = orientation === 'horizontal';

  return (
    <Box
      component={pathname ? Link : 'button'}
      aria-label={ariaLabel}
      data-testid="app-bar-button"
      data-active={active}
      data-disabled={disabled}
      display="flex"
      flexDirection="row"
      px={1.25}
      py={isHorizontal ? 1 : 0}
      gap={isHorizontal ? 1.5 : 0.75}
      justifyItems="stretch"
      alignItems="center"
      onClick={onClick}
      {...(isHorizontal ? { width: '100%' } : { height: 32 })}
      href={pathname}
      sx={{
        cursor: 'pointer',
        // `text.secondary`, not the `GrayText` system colour, so the row reads
        // as the same ink as the search pill it sits beside.
        color: 'text.secondary',
        textDecoration: 'none',
        backgroundColor: 'inherit',
        border: 'none',
        borderRadius: `${BUTTON_RADIUS}px`,
        ...(outlined && {
          border: '1px solid',
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.4),
          backgroundColor: 'background.paper',
          color: 'primary.main',
        }),
        justifyContent: isHorizontal ? 'flex-start' : undefined,
        // The bar is a single scrollable row of pills, so a tile must neither shrink nor
        // wrap in it.
        flexShrink: 0,
        whiteSpace: 'nowrap',
        transition: 'background-color 0.15s, color 0.15s',

        // When disabled, the cursor should indicate this.
        '&[data-disabled="true"]': { cursor: 'not-allowed' },
        '&[data-disabled="false"]:hover': {
          backgroundColor: HOVER_COLOR,
          color: ACTIVE_COLOR,
          ...(outlined && { borderColor: ACTIVE_COLOR }),
        },

        // Turn the text and icon the `ACTIVE_COLOR` when the `AppBarButton` is
        // considered `active`.
        '&[data-active="true"]': { color: ACTIVE_COLOR, backgroundColor: HOVER_COLOR },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>{icon}</Box>
      <Typography
        component="span"
        sx={{
          typography: 'body2',
          whiteSpace: 'nowrap',
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}
